import React, { useState, useEffect } from 'react';
import { Map, Route, MapPin } from 'lucide-react';
import Navigation from '../components/common/Navigation';
import UserLocationMap from '../components/Maps';
import MapControls from '../components/Maps/mapcontrol';
import ToastMessage from '../components/ToastMessage';
import PlanningIndicator from '../components/Maps/planIndicator';
import api from '../request'; // Adjust the import based on your project structure
const Mappage = () => {
  const [toast, setToast] = useState({ message: '', type: '' });
  const [planningMode, setPlanningMode] = useState(false);
  const [startLocation, setStartLocation] = useState(() => {
  const saved = localStorage.getItem('startLocation');
  return saved ? JSON.parse(saved) : '';
});

  const [activityId, setActivityId] = useState(() => {
    const saved = localStorage.getItem('activityId');
    return saved ? JSON.parse(saved) : '';
  });

  const [endLocation, setEndLocation] = useState(null);
  const [plannedRoute, setPlannedRoute] = useState(null);

  const [currentRoute, setCurrentRoute] = useState(null);

  const [tracking, setTracking] = useState(() => {
    const saved = localStorage.getItem('tracking');
    return saved ? JSON.parse(saved) : false;
  });

 const [routeHistory, setRouteHistory] = useState([]);


  const [routeCoords, setRouteCoords] = useState(() => {
    const saved = localStorage.getItem('routeCoords');
    return saved ? JSON.parse(saved) : [];
  });

  const [stats, setStats] = useState(() => {
    const saved = localStorage.getItem('trackingStats');
    return saved ? JSON.parse(saved) : {
      currentSteps: 0,
      currentDistance: 0,
      currentDuration: 0,
      pace: 0,
      _startTime: null,
    };
  });

  const [weather, setWeather] = useState({
    temp: 18,
    condition: 'Partly Cloudy',
    humidity: 65,
    wind: 10
  });
  const [showRouteHistory, setShowRouteHistory] = useState(false);
  
  const [showNearby, setShowNearby] = useState(false);  // Toggle visibility of nearby locations
  const [nearbyMarkers, setNearbyMarkers] = useState([]);  // Store fetched nearby markers
  const [selectedPlaceType, setSelectedPlaceType] = useState('park');  // Default to parks
  const [indicatorVisible, setIndicatorVisible] = useState(true);


  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: '' }), 4000);
  };

  const handleMapClick = async (e) => {
    const coord = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };

    console.log("Map clicked at:", coord);
  
    if (!startLocation) {
      setStartLocation(coord);
    } else if (!endLocation) {
      setEndLocation(coord);
  
      // Fetch directions
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: startLocation,
          destination: coord,
          travelMode: window.google.maps.TravelMode.WALKING,
        },
        (result, status) => {
          if (status === "OK") {
            setPlannedRoute(result);
          } else {
            console.error("Failed to get directions:", status);
          }
        }
      );
      setIndicatorVisible(false);
    }
  };
  

  const handleCoordinateUpdate = (coord) => {
    setRouteCoords((prev) => {
      if (prev.length > 0) {
        const last = prev[prev.length - 1];
        const distance = calculateDistanceKm(last.lat, last.lng, coord.lat, coord.lng);
  
        // Ignore GPS jumps under 10 meters
        if (distance < 0.01) return prev;
  
        setStats((s) => {
          const newDistance = +(s.currentDistance + distance).toFixed(3);
          const newSteps = s.currentSteps + Math.round(distance * 1200);
          const newPace = +(newDistance / (s.currentDuration / 60)).toFixed(2);
  
          return {
            ...s,
            currentDistance: newDistance,
            currentSteps: newSteps,
            pace: isFinite(newPace) ? newPace : 0
          };
        });
      }
  
      return [...prev, coord];
    });
  };
  
  
  
  

  function calculateDistanceKm(lat1, lon1, lat2, lon2) {
    const R = 6371; // km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }
  
  const startTracking = () => {
    const now = Date.now();
    setStats({
      currentSteps: 0,
      currentDistance: 0,
      currentDuration: 0,
      pace: 0,
      _startTime: now,
        lat : 0, 
        lng: 0
    });
    setRouteCoords([]);
    setTracking(true);
    setShowRouteHistory(false);
  };
  

  // Mock tracking functionality
  useEffect(() => {
    let timer;
  
    if (tracking && stats._startTime) {
      timer = setInterval(() => {
        setStats((s) => {
          const now = Math.floor((Date.now() - s._startTime) / 1000);
          return { ...s, currentDuration: now };
        });
      }, 1000);
    }
  
    return () => clearInterval(timer);
  }, [tracking, stats._startTime]);
  
  // Fetch walking route between start and end when both are set
useEffect(() => {
  if (planningMode && startLocation && endLocation) {
    const service = new window.google.maps.DirectionsService();

    service.route(
      {
        origin: startLocation,
        destination: endLocation,
        travelMode: window.google.maps.TravelMode.WALKING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setPlannedRoute(result);
        } else {
          console.error('Error fetching directions:', result);
          setPlannedRoute(null);
        }
      }
    );
  }
}, [planningMode, startLocation, endLocation]);

 
  

  const stopTracking = () => {
    setTracking(false);
    resetTrackingSession();
    // In a real app, we would save the route here
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' + secs : secs}`;
  };

  const selectRoute = (routeId) => {
    const selected = routeHistory.find(route => route.id === routeId);
    // In a real app, we would load the route coordinates here
  
  };
 
  const saveRoute = async () => {
    console.log("Saving route, routeCoords:", routeCoords);

    if (stats.currentSteps < 1 || routeCoords.length < 2) {
      showToast("Not enough data to save.", "error");
      
      return;
    }
  
    const start = routeCoords[0];
    const end = routeCoords[routeCoords.length - 1];
    const now = new Date();
  
    const walkName = new Intl.DateTimeFormat('en-US', {
      weekday: 'long', hour: 'numeric', minute: 'numeric'
    }).format(now) + ' Walk';
  
    try {

      const res = await api.post('/activities', {
        name: walkName,
        start_time: new Date(stats._startTime).toISOString(),
        end_time: now.toISOString(),
        duration: stats.currentDuration,
        distance_km: stats.currentDistance,
        steps: stats.currentSteps,
        avg_pace_kmh: stats.pace,
        start_lat: start.lat,
        start_lng: start.lng,
        end_lat: end.lat,
        end_lng: end.lng,
        path_data: JSON.stringify(routeCoords),
        calories: Math.round(stats.currentDistance * 60), // Approx 60 kcal per km
        weather_summary: `${weather.temp}°C, ${weather.condition}`
      });
      
      showToast("Activity saved successfully!");
      setTracking(false);
      resetTrackingSession();
    } catch (error) {
      console.error("Failed to save activity:", error);
      showToast("Something went wrong saving your walk.", "error");
    }
  };
  

  const toggleRouteHistory = () => {
    setShowRouteHistory(!showRouteHistory);
  };

    // Persist stats
  useEffect(() => {
    localStorage.setItem('trackingStats', JSON.stringify(stats));
  }, [stats]);

  // Persist routeCoords
  useEffect(() => {
    localStorage.setItem('routeCoords', JSON.stringify(routeCoords));
  }, [routeCoords]);

  // Persist tracking flag
  useEffect(() => {
    localStorage.setItem('tracking', JSON.stringify(tracking));
  }, [tracking]);

  // Persist start location
  useEffect(() => {
    if (startLocation) {
      localStorage.setItem('startLocation', JSON.stringify(startLocation));
    }
  }, [startLocation]);

  // Persist activity ID
  useEffect(() => {
    if (activityId) {
      localStorage.setItem('activityId', JSON.stringify(activityId));
    }
  }, [activityId]);

  const resetTrackingSession = () => {
    setStats({
      currentSteps: 0,
      currentDistance: 0,
      currentDuration: 0,
      pace: 0,
      _startTime: null,
    });

    setRouteCoords([]);
    setTracking(false);
    setStartLocation(null);
    setActivityId(null);

    localStorage.removeItem('trackingStats');
    localStorage.removeItem('routeCoords');
    localStorage.removeItem('tracking');
    localStorage.removeItem('startLocation');
    localStorage.removeItem('activityId');
  };

  useEffect(() => {
  const fetchRouteHistory = async () => {
    try {
      const res = await api.get('/user/route-history');
      setRouteHistory(res.data);
    } catch (err) {
      console.error("Error fetching route history:", err);
    }
  };

  fetchRouteHistory();
}, []);



  return (
    <div className="flex h-screen bg-background-light">
      <ToastMessage
      message={toast.message}
      type={toast.type}
      onClose={() => setToast({ message: '', type: '' })}
    />

      {/* Left sidebar navigation - desktop only */}
      <div className="hidden md:flex flex-col w-64 border-r border-gray-200">
        <Navigation />
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col">
        {/* Top header */}
        <header className="bg-white shadow-sm p-2 md:p-4 flex justify-between items-center">
          <h1 className="text-lg md:text-xl font-semibold">Walking Map</h1>
          <div className="flex items-center space-x-2 md:space-x-4">
            <div className="text-xs md:text-sm text-gray-500">
              <span className="font-medium text-primary">{tracking ? 'Recording' : 'Ready'}</span>
            </div>
            <button 
              onClick={toggleRouteHistory} 
              className="md:hidden bg-gray-100 p-1 rounded-full"
            >
              <Route size={18} />
            </button>
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white">
              <Map size={18} />
            </div>
          </div>
        </header>

        {/* Map and controls */}
        <div className="flex-1 flex flex-col md:flex-row relative">
            {/* The map itself - full screen on mobile */}
            <div className="flex-1 bg-gray-100 relative">
              {/* Map component - full width and height */}
              <div className="absolute inset-0">
                {/* <UserLocationMap /> */}

                <UserLocationMap
                  tracking={tracking}
                  onCoordinateUpdate={handleCoordinateUpdate}
                  routeCoords={routeCoords}
                  planningMode={planningMode}
                  onMapClick={handleMapClick}
                  plannedRoute={plannedRoute}
                  startLocation={startLocation}     
                  endLocation={endLocation}  
                  showNearby={showNearby}  // Pass the state here
                  setNearbyMarkers={setNearbyMarkers}
                  nearbyMarkers={nearbyMarkers}  
                  selectedPlaceType={selectedPlaceType}
              />

              <MapControls
                tracking={tracking}
                startTracking={startTracking}
                stopTracking={stopTracking}
                saveRoute={saveRoute}
                toggleRouteHistory={toggleRouteHistory}
                setPlanningMode={setPlanningMode}
                planningMode={planningMode}
                plannedRoute={plannedRoute}
                setShowNearby={setShowNearby}
                showNearby={showNearby}
                selectedPlaceType={selectedPlaceType}
                setSelectedPlaceType={setSelectedPlaceType} 
                startPlannedRoute={() => {
                  setTracking(true);
                  setStats({
                    currentSteps: 0,
                    currentDistance: 0,
                    currentDuration: 0,
                    pace: 0,
                    _startTime: Date.now(),
                    lat : 0, 
                    lng: 0
                  });
                  setRouteCoords([]);
                  showToast("Route started. Tracking now.");
                }}
                cancelPlanning={() => {
                  setPlanningMode(false);
                  setStartLocation(null);
                  setEndLocation(null);
                  setPlannedRoute(null);
                }}
              />

              <PlanningIndicator 
                planningMode={planningMode} 
                startLocation={startLocation} 
                isVisible={indicatorVisible}
                setIsVisible={setIndicatorVisible}
              />

              </div>

              {/* Floating stats overlay - smaller on mobile */}
              <div className="absolute top-2 left-2 right-2 bg-white rounded-lg shadow-md p-2 md:p-4 text-sm md:text-base">
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <p className="text-xs text-gray-500">Distance</p>
                    <p className="text-base md:text-xl font-bold">{stats.currentDistance.toFixed(2)}<span className="text-xs font-normal">km</span></p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Steps</p>
                    <p className="text-base md:text-xl font-bold">{stats.currentSteps.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Duration</p>
                    <p className="text-base md:text-xl font-bold">{formatDuration(stats.currentDuration)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Pace</p>
                    <p className="text-base md:text-xl font-bold">{stats.pace}<span className="text-xs font-normal">km/h</span></p>
                  </div>
                </div>
              </div>

              {/* Weather overlay - smaller on mobile */}
              {/* <div className="absolute bottom-20 md:bottom-4 left-2 bg-white rounded-lg shadow-md p-2">
                <div className="flex items-center">
                  <div className="mr-2">
                    <svg className="w-6 h-6 md:w-8 md:h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm font-medium">{weather.condition}</p>
                    <div className="flex text-xs text-gray-500">
                      <p>{weather.temp}°C</p>
                      <p className="mx-1">•</p>
                      <p>H:{weather.humidity}%</p>
                      <p className="mx-1">•</p>
                      <p>W:{weather.wind}km/h</p>
                    </div>
                  </div>
                </div>
              </div> */}

              {/* Floating tracking controls for mobile */}
              {/* <div className="md:hidden absolute bottom-20 right-2 flex flex-col space-y-2">
                {!tracking ? (
                  <button 
                    onClick={startTracking}
                    className="bg-primary text-white p-3 rounded-full shadow-lg"
                  >
                    <Route size={24} />
                  </button>
                ) : (
                  <>
                    <button 
                      onClick={saveRoute}
                      className="bg-secondary text-white p-3 rounded-full shadow-lg"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                        <polyline points="17 21 17 13 7 13 7 21"></polyline>
                        <polyline points="7 3 7 8 15 8"></polyline>
                      </svg>
                    </button>
                    <button 
                      onClick={stopTracking}
                      className="bg-red-500 text-white p-3 rounded-full shadow-lg"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="6" y="6" width="12" height="12" rx="2" ry="2"></rect>
                      </svg>
                    </button>
                  </>
                )}

              

              </div> */}
            </div>

            {/* Right sidebar with route details and controls - desktop only */}
            <div className="hidden md:block w-80 bg-white border-l border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h2 className="font-semibold">Route  Controls</h2>
              </div>

              <div className="p-4 border-t border-gray-200">
                  <div className="flex flex-col space-y-3">
                    <h3 className="text-sm font-medium">Nearby Places</h3>
                    <div className="flex flex-wrap gap-2">
                      <button 
                        onClick={() => {
                          setSelectedPlaceType('park');
                          setShowNearby(true);
                        }}
                        className={`px-3 py-1 text-sm rounded-full flex items-center ${
                          showNearby && selectedPlaceType === 'park' 
                            ? 'bg-green-500 text-white' 
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        <span className="mr-1">🌳</span> Parks
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedPlaceType('hiking');
                          setShowNearby(true);
                        }}
                        className={`px-3 py-1 text-sm rounded-full flex items-center ${
                          showNearby && selectedPlaceType === 'hiking' 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                      >
                        <span className="mr-1">🥾</span> Trails
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedPlaceType('gym');
                          setShowNearby(true);
                        }}
                        className={`px-3 py-1 text-sm rounded-full flex items-center ${
                          showNearby && selectedPlaceType === 'gym' 
                            ? 'bg-purple-500 text-white' 
                            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                        }`}
                      >
                        <span className="mr-1">💪</span> Gyms
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedPlaceType('cafe');
                          setShowNearby(true);
                        }}
                        className={`px-3 py-1 text-sm rounded-full flex items-center ${
                          showNearby && selectedPlaceType === 'cafe' 
                            ? 'bg-yellow-500 text-white' 
                            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        }`}
                      >
                        <span className="mr-1">☕</span> Cafés
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedPlaceType('rest_stop');
                          setShowNearby(true);
                        }}
                        className={`px-3 py-1 text-sm rounded-full flex items-center ${
                          showNearby && selectedPlaceType === 'rest_stop' 
                            ? 'bg-red-500 text-white' 
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        <span className="mr-1">🚻</span> Rest Stops
                      </button>
                      
                      {showNearby && (
                        <button 
                          onClick={() => setShowNearby(false)}
                          className="px-3 py-1 text-sm rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                  </div>
                </div>

              {/* paling of route  */}
              <div className="p-4 border-t border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center md:space-x-3 space-y-2 md:space-y-0">
                    {/* <button 
                      onClick={() => setShowNearby(!showNearby)} 
                      className="bg-blue-100 text-blue-700 px-3 py-1 rounded"
                    >
                      {showNearby ? 'Hide Nearby Locations' : 'Show Nearby Locations'}
                    </button> */}

                  {planningMode && (
                    <button
                      onClick={() => {
                        setPlanningMode(false);
                        setStartLocation(null);
                        setEndLocation(null);
                        setPlannedRoute(null);
                      }}
                      className="text-red-600 text-sm font-medium hover:underline"
                    >
                      Cancel Planning
                    </button>
                  )}

                  {/* {plannedRoute && !tracking && (
                    <button
                      onClick={() => {
                        setTracking(true);
                        setStats({
                          currentSteps: 0,
                          currentDistance: 0,
                          currentDuration: 0,
                          pace: 0,
                          _startTime: Date.now()
                        });
                        setRouteCoords([]);
                        showToast("Route started. Tracking now.");
                      }}
                      className="bg-primary hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-semibold shadow-sm"
                    >
                      🚶 Start Planned Route
                    </button>
                  )} */}

                  {!tracking && !plannedRoute && (
                    <button
                      onClick={() => {
                           setPlanningMode(true)
                        setIndicatorVisible(true)
                      }
                       
                      }
                      className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-2 rounded-md text-sm font-medium"
                    >
                      ✏️ Plan New Route
                    </button>
                  )}
                </div>
                
              </div>
              {/* end of planing */}

              <div className="p-4 flex space-x-2">
                {!tracking ? (
                  <button 
                    onClick={startTracking}
                    className="flex-1 bg-primary text-white py-2 px-4 rounded-md flex items-center justify-center space-x-1"
                  >
                    <Route size={18} />
                    <span>Start Route</span>
                  </button>
                ) : (
                  <>
                    <button 
                      onClick={stopTracking}
                      className="flex-1 bg-red-500 text-white py-2 px-4 rounded-md flex items-center justify-center space-x-1"
                    >
                      <span>Stop</span>
                    </button>
                    <button 
                      onClick={saveRoute}
                      className="flex-1 bg-secondary text-white py-2 px-4 rounded-md flex items-center justify-center space-x-1"
                    >
                      <span>Save</span>
                    </button>
                  </>
                )}
              </div>



              <div className="p-4 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Route History</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {routeHistory.map(route => (
                    <div 
                      key={route.id}
                      onClick={() => selectRoute(route.id)}
                      className="p-3 bg-gray-50 rounded-md cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex justify-between items-center">
                        <h4 className="font-medium">{route.name}</h4>
                        <span className="text-xs text-gray-500">{route.date}</span>
                      </div>
                      <div className="flex text-sm text-gray-500 mt-1">
                        <p>{route.distance} km</p>
                        <p className="mx-1">•</p>
                        <p>{route.steps.toLocaleString()} steps</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Mobile Route History Drawer - slides up from bottom */}
            {showRouteHistory && (
              <div className="md:hidden absolute inset-0 bg-white z-10 flex flex-col">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="font-semibold">Route History</h3>
                  <button onClick={toggleRouteHistory} className="p-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-2">
                    {routeHistory.map(route => (
                      <div 
                        key={route.id}
                        onClick={() => {
                          selectRoute(route.id);
                          setShowRouteHistory(false);
                        }}
                        className="p-3 bg-gray-50 rounded-md cursor-pointer hover:bg-gray-100"
                      >
                        <div className="flex justify-between items-center">
                          <h4 className="font-medium">{route.name}</h4>
                          <span className="text-xs text-gray-500">{route.date}</span>
                        </div>
                        <div className="flex text-sm text-gray-500 mt-1">
                          <p>{route.distance} km</p>
                          <p className="mx-1">•</p>
                          <p>{route.steps.toLocaleString()} steps</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
        </div>

        {/* Mobile navigation - only visible on small screens */}
        <div className="md:hidden block sticky bottom-0 z-50 bg-white border-t border-gray-200 p-2">
      
          <Navigation orientation='horizontal' />
        </div>
      </div>
    </div>
  );
};

export default Mappage;